import { useState, type FormEvent } from 'react';
import {
  Box, Stack, TextField, Button, MenuItem, Alert,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EntryCategoryLabels, type EntryCategory, type Entry } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';

const CATEGORIES: EntryCategory[] = ['note', 'med', 'meal', 'appointment', 'symptom', 'handover'];

export default function EntryForm({ onCreated }: { onCreated?: () => void }) {
  const [category, setCategory] = useState<EntryCategory>('note');
  const [body, setBody] = useState('');
  const [medName, setMedName] = useState('');
  const [medDose, setMedDose] = useState('');
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: { category: EntryCategory; body: string; medName?: string; medDose?: string }) =>
      api.post<Entry>('/api/entries', input),
    onSuccess: () => {
      setBody('');
      setMedName('');
      setMedDose('');
      qc.invalidateQueries({ queryKey: ['entries'] });
      onCreated?.();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    mutation.mutate({
      category,
      body: body.trim(),
      medName: category === 'med' ? (medName.trim() || undefined) : undefined,
      medDose: category === 'med' ? (medDose.trim() || undefined) : undefined,
    });
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Stack spacing={2}>
        <TextField
          select
          size="small"
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value as EntryCategory)}
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>{EntryCategoryLabels[c]}</MenuItem>
          ))}
        </TextField>

        {category === 'med' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Medication"
              value={medName}
              onChange={(e) => setMedName(e.target.value)}
              sx={{ flex: 2 }}
            />
            <TextField
              label="Dose"
              value={medDose}
              onChange={(e) => setMedDose(e.target.value)}
              sx={{ flex: 1 }}
              placeholder="e.g. 5mg"
            />
          </Stack>
        )}

        <TextField
          label="What's happening?"
          multiline
          minRows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          fullWidth
        />

        {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}

        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending || !body.trim()}
          >
            {mutation.isPending ? 'Saving…' : 'Add entry'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
